import {useEffect, useState} from "react";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import {Badge, Box, useMediaQuery} from "@mui/material";
import type {BadgeProps} from "@mui/material";
import {keyframes} from "@emotion/react";
import {useTranslation} from "react-i18next";
import {useAssistant} from "./AssistantPanelContext";
import AssistantReplyCard from "./AssistantReplyCard";
import {
    ASSISTANT_FAB_MARGIN,
    ASSISTANT_FAB_MOBILE_MARGIN,
    ASSISTANT_FAB_MOBILE_SIZE,
    ASSISTANT_FAB_SIZE,
} from "./constants";

// How long the launcher animates after a reply lands, before settling into the static coloured ring.
const ANNOUNCE_DURATION_MS = 10_000;

const halo = keyframes`
    0%        { transform: scale(1); opacity: 0.5; }
    70%, 100% { transform: scale(2.1); opacity: 0; }
`;

const breathe = keyframes`
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.4; }
`;

const AssistantFab = () => {
    const {t} = useTranslation();
    const assistant = useAssistant();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const status = assistant?.status;
    const [announcing, setAnnouncing] = useState(false);

    // Motion announces an arrival, and only an arrival. Waiting on the team can last hours, and a
    // launcher pulsing in the corner all afternoon is its own kind of awful — that state gets the
    // static amber ring, which says "the team has it" without ever moving. A reply gets ten seconds
    // of animation and then settles into the same static ring.
    useEffect(() => {
        if (status !== "unreadAnswer") {
            setAnnouncing(false);
            return;
        }
        setAnnouncing(true);
        const timer = window.setTimeout(() => setAnnouncing(false), ANNOUNCE_DURATION_MS);
        return () => window.clearTimeout(timer);
    }, [status]);

    if (!assistant) return null;

    const {isOpen, toggle} = assistant;
    const dotColor: BadgeProps["color"] = isOpen
        ? undefined
        : status === "unreadAnswer"
            ? "error"
            : status === "waitingForHuman"
                ? "warning"
                : status === "asking"
                    ? "info"
                    : undefined;
    const iconSx = {
        position: "absolute" as const,
        top: "50%",
        left: "50%",
        fontSize: {xs: 22, sm: 26},
        transition: "opacity 0.25s, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    };

    // A ring around the whole button, not just the 12px dot — the dot alone was being
    // missed. Amber = the team has it, red = they answered.
    const ringColor = isOpen
        ? undefined
        : status === "unreadAnswer"
            ? "#D32F2F"
            : status === "waitingForHuman"
                ? "#EF6C00"
                : undefined;
    const announceReply = !!ringColor && announcing && !prefersReducedMotion;

    return (
        <>
        <AssistantReplyCard/>
        <Badge
            color={dotColor ?? "default"}
            variant="dot"
            overlap="circular"
            invisible={!dotColor}
            className="hide-on-print"
            sx={{
                position: "fixed",
                bottom: {xs: ASSISTANT_FAB_MOBILE_MARGIN, sm: ASSISTANT_FAB_MARGIN},
                right: {xs: ASSISTANT_FAB_MOBILE_MARGIN, sm: ASSISTANT_FAB_MARGIN},
                zIndex: 1250,
                "& .MuiBadge-badge": {border: "2px solid #fff"},
                "& .MuiBadge-dot": {
                    width: {xs: 10, sm: 12},
                    height: {xs: 10, sm: 12},
                    borderRadius: "50%",
                },
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={toggle}
                aria-label={isOpen ? t("Close Crqlar Assistant") : t("Open Crqlar Assistant")}
                aria-pressed={isOpen}
                sx={{
                    width: {xs: ASSISTANT_FAB_MOBILE_SIZE, sm: ASSISTANT_FAB_SIZE},
                    height: {xs: ASSISTANT_FAB_MOBILE_SIZE, sm: ASSISTANT_FAB_SIZE},
                    p: 0,
                    border: 0,
                    borderRadius: "50%",
                    position: "relative",
                    cursor: "pointer",
                    color: "#fff",
                    bgcolor: "primary.main",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.28)",
                    transition: "background-color 150ms ease, transform 150ms ease, box-shadow 150ms ease",
                    "&:hover": {
                        bgcolor: "primary.dark",
                        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.34)",
                    },
                    "&:active": {transform: "scale(0.94)"},
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.light",
                        outlineOffset: 3,
                    },
                }}
            >
                <ChatIcon
                    sx={{
                        ...iconSx,
                        opacity: isOpen ? 0 : 1,
                        transform: isOpen
                            ? "translate(-50%, -50%) rotate(90deg)"
                            : "translate(-50%, -50%) rotate(0deg)",
                    }}
                />
                <CloseIcon
                    sx={{
                        ...iconSx,
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen
                            ? "translate(-50%, -50%) rotate(0deg)"
                            : "translate(-50%, -50%) rotate(-90deg)",
                    }}
                />
                {ringColor && (
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: "-3px",
                            borderRadius: "50%",
                            border: `2px solid ${ringColor}`,
                            pointerEvents: "none",
                            animation: announceReply
                                ? `${breathe} 2.4s ease-in-out infinite`
                                : "none",
                        }}
                    />
                )}
                {announceReply && (
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            border: `2px solid ${ringColor}`,
                            pointerEvents: "none",
                            animation: `${halo} 1.5s ease-out infinite`,
                        }}
                    />
                )}
            </Box>
        </Badge>
        </>
    );
};

export default AssistantFab;
